import React, { useState } from 'react';
import { toast } from '../../../components/shared/toast';
import { apiClient } from '../../../api/apiClient';
import './CaseDetails.css';

function CaseDetails({ item, onBack, t }) {
  const todayStr = new Date().toISOString().split('T')[0];
  const [stages, setStages] = useState(item.stages || item.situations || []);
  const [isAddingStage, setIsAddingStage] = useState(false);
  const [newStageStartDate, setNewStageStartDate] = useState(todayStr);
  const [newStageEndDate, setNewStageEndDate] = useState(todayStr);
  const [newStageDesc, setNewStageDesc] = useState('');

  const handleOpenInlineAdd = () => {
    setNewStageStartDate(todayStr);
    setNewStageEndDate(todayStr);
    setNewStageDesc('');
    setIsAddingStage(true);
  };

  const handleSaveInlineStage = async () => {
    if (!newStageStartDate) {
      toast.warning('Please select start date.');
      return;
    }
    if (!newStageEndDate) {
      toast.warning('Please select end date.');
      return;
    }
    if (newStageEndDate < newStageStartDate) {
      toast.warning('End date cannot be before start date.');
      return;
    }
    if (!newStageDesc || !newStageDesc.trim()) {
      toast.warning('Please enter stage description.');
      return;
    }

    try {
      const res = await apiClient(`/notes-and-cases/${item.id}/situations`, {
        method: 'POST',
        body: {
          start_date: newStageStartDate,
          end_date: newStageEndDate,
          description: newStageDesc.trim()
        }
      });

      const saved = res?.situation || {};
      const nextNum = (stages.length + 1).toString().padStart(2, '0');
      const newStage = {
        label: saved.label || (stages.length === 0 ? 'start' : nextNum),
        startDate: saved.start_date || newStageStartDate,
        start_date: saved.start_date || newStageStartDate,
        endDate: saved.end_date || newStageEndDate,
        end_date: saved.end_date || newStageEndDate,
        date: saved.start_date || newStageStartDate,
        description: saved.description || newStageDesc.trim()
      };

      setStages([...stages, newStage]);
      setNewStageDesc('');
      setIsAddingStage(false);
      toast.success('Stage added to situation timeline.');
    } catch (err) {
      console.error('Error saving situation:', err);
      toast.error('Failed to save situation. Please try again.');
    }
  };
  
  // Situation Modal state
  const [showSituationModal, setShowSituationModal] = useState(false);
  const [sitStartDate, setSitStartDate] = useState(todayStr);
  const [sitEndDate, setSitEndDate] = useState(todayStr);
  const [sitDescription, setSitDescription] = useState('');
  const [sitError, setSitError] = useState('');

  // Edit inline state
  const [editingIdx, setEditingIdx] = useState(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editDesc, setEditDesc] = useState('');

  // Delete confirm state
  const [deletingIdx, setDeletingIdx] = useState(null);

  const handleOpenEdit = (idx) => {
    const s = stages[idx];
    setEditingIdx(idx);
    setEditStartDate(s.startDate || s.start_date || s.date || todayStr);
    setEditEndDate(s.endDate || s.end_date || todayStr);
    setEditDesc(s.description || '');
  };

  const handleCancelEdit = () => {
    setEditingIdx(null);
    setEditStartDate('');
    setEditEndDate('');
    setEditDesc('');
  };

  const handleSaveEdit = async (idx) => {
    if (!editStartDate) { toast.warning('Please select start date.'); return; }
    if (!editEndDate) { toast.warning('Please select end date.'); return; }
    if (editEndDate < editStartDate) { toast.warning('End date cannot be before start date.'); return; }
    if (!editDesc.trim()) { toast.warning('Please enter description.'); return; }

    const stage = stages[idx];
    const sitId = stage.id || stage.situation_id;

    try {
      if (sitId) {
        await apiClient(`/notes-and-cases/${item.id}/situations/${sitId}`, {
          method: 'PUT',
          body: { start_date: editStartDate, end_date: editEndDate, description: editDesc.trim() }
        });
      }
      const updated = stages.map((s, i) =>
        i === idx
          ? { ...s, startDate: editStartDate, start_date: editStartDate, endDate: editEndDate, end_date: editEndDate, description: editDesc.trim() }
          : s
      );
      setStages(updated);
      handleCancelEdit();
      toast.success('Situation updated successfully.');
    } catch (err) {
      console.error('Error updating situation:', err);
      toast.error('Failed to update situation. Please try again.');
    }
  };

  const handleConfirmDelete = async (idx) => {
    const stage = stages[idx];
    const sitId = stage.id || stage.situation_id;

    try {
      if (sitId) {
        await apiClient(`/notes-and-cases/${item.id}/situations/${sitId}`, { method: 'DELETE' });
      }
      setStages(stages.filter((_, i) => i !== idx));
      setDeletingIdx(null);
      toast.success('Situation deleted.');
    } catch (err) {
      console.error('Error deleting situation:', err);
      toast.error('Failed to delete situation. Please try again.');
    }
  };

  const handleOpenAddSituation = () => {
    setSitStartDate(todayStr);
    setSitEndDate(todayStr);
    setSitDescription('');
    setSitError('');
    setShowSituationModal(true);
  };

  const handleSaveSituationModal = async () => {
    if (!sitStartDate) {
      setSitError('Please select situation start date.');
      toast.warning('Please select situation start date.');
      return;
    }
    if (!sitEndDate) {
      setSitError('Please select situation end date.');
      toast.warning('Please select situation end date.');
      return;
    }
    if (sitEndDate < sitStartDate) {
      setSitError('Situation end date cannot be before start date.');
      toast.warning('Situation end date cannot be before start date.');
      return;
    }
    if (!sitDescription || !sitDescription.trim()) {
      setSitError('Please enter situation description.');
      toast.warning('Please enter situation description.');
      return;
    }

    try {
      setSitError('');
      const res = await apiClient(`/notes-and-cases/${item.id}/situations`, {
        method: 'POST',
        body: {
          start_date: sitStartDate,
          end_date: sitEndDate,
          description: sitDescription.trim()
        }
      });

      const saved = res?.situation || {};
      const nextNum = (stages.length + 1).toString().padStart(2, '0');
      const newStage = {
        label: saved.label || (stages.length === 0 ? 'start' : nextNum),
        startDate: saved.start_date || sitStartDate,
        start_date: saved.start_date || sitStartDate,
        endDate: saved.end_date || sitEndDate,
        end_date: saved.end_date || sitEndDate,
        date: saved.start_date || sitStartDate,
        description: saved.description || sitDescription.trim()
      };

      setStages([...stages, newStage]);
      setShowSituationModal(false);
      toast.success('Situation stage added successfully.');
    } catch (err) {
      console.error('Error saving situation:', err);
      setSitError('Failed to save situation. Please try again.');
      toast.error('Failed to save situation. Please try again.');
    }
  };

  const linkTypeLabel = item.linkType || item.link_type || 'Link';
  const linkResultLabel = item.linkResult || item.link_result || item.linkedValue || 'None';

  return (
    <div className="case-details-wrapper">
      {/* Breadcrumb Nav */}
      <div className="breadcrumb-nav">
        <span className="breadcrumb-link" onClick={onBack}>
          {t('notesCases.breadcrumb') || 'Note & Cases'}
        </span>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">{item.name}</span>
      </div>

      {/* Case Details Card */}
      <div className="case-details-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <h2 className="case-detail-name" style={{ margin: 0 }}>{item.name}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {(item.status === 0 || item.status === '0') && (
              <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', backgroundColor: '#fee2e2', color: '#ef4444', border: '1px solid #fca5a5', padding: '3px 10px', borderRadius: '12px' }}>
                CLOSED
              </span>
            )}
            {item.priority && (
              <span className={`priority-badge ${item.priority}`} style={{ textTransform: 'uppercase', fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '12px' }}>
                {item.priority} Priority
              </span>
            )}
          </div>
        </div>

        <div className="case-detail-tags">
          {item.type === 'case' ? 'Case Type' : 'Note Type'} - tags - {item.tags || 'Youtube, name'}
        </div>
        <p className="case-detail-description">
          {item.description || 'No description recorded.'}
        </p>

        {/* Link Section */}
        <div className="case-link-section">
          <div className="case-link-title-row">
            <span className="case-link-title">
              {t('notesCases.details.linkLabel') || 'Link Information'}
            </span>
          </div>

          <div className="case-link-list">
            <div className="case-link-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <span>{linkTypeLabel}: {linkResultLabel}</span>
            </div>

            {item.startDate && (
              <div className="case-link-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                </svg>
                <span>Start Date: {item.startDate || item.start_date}</span>
              </div>
            )}

            {item.type === 'case' && item.endDate && (
              <div className="case-link-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                </svg>
                <span>End Date: {item.endDate || item.end_date}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Situation / Timeline Section (Only for cases) */}
      {item.type === 'case' && (
        <div className="case-situation-section">
          { (item.status === 0 || item.status === '0' || item.is_delete === 1) && (
            <div style={{ padding: '10px 14px', borderRadius: '8px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', fontSize: '13px', fontWeight: '600' }}>
              🔒 This case is CLOSED. New situations cannot be added and existing situations cannot be modified.
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 className="situation-title" style={{ margin: 0 }}>
              {t('notesCases.details.situationHeading') || 'Situation Timeline'}
            </h3>
            {!(item.status === 0 || item.status === '0' || item.is_delete === 1) && (
              <button 
                type="button" 
                className="btn-create-situation"
                onClick={handleOpenAddSituation}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '8px', backgroundColor: '#0b66e3', color: '#ffffff', border: 'none', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>Add Situation</span>
              </button>
            )}
          </div>

          <div className="situation-timeline-row">
            {stages.map((stage, idx) => (
              <React.Fragment key={idx}>
                {/* Stage Box */}
                <div className="situation-stage-box">
                  {/* Header */}
                  <div className="stage-box-header">
                    <span className="stage-label-text">
                      {stage.label || (idx === 0 ? 'start' : String(idx + 1).padStart(2, '0'))}
                    </span>

                    <span className="stage-date-range">
                      {stage.startDate || stage.start_date || stage.date || ''}
                      {(stage.endDate || stage.end_date) && (stage.endDate || stage.end_date) !== (stage.startDate || stage.start_date)
                        ? ` — ${stage.endDate || stage.end_date}`
                        : ''}
                    </span>

                    {editingIdx !== idx && !(item.status === 0 || item.status === '0' || item.is_delete === 1) && (
                      <div className="stage-header-actions">
                        {/* Edit Button */}
                        <button
                          type="button"
                          className="stage-action-btn edit-btn"
                          onClick={() => handleOpenEdit(idx)}
                          title="Edit situation"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        {/* Delete Button */}
                        <button
                          type="button"
                          className="stage-action-btn delete-btn"
                          onClick={() => setDeletingIdx(idx)}
                          title="Delete situation"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14H6L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4h6v2" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Inline Edit Form */}
                  {editingIdx === idx ? (
                    <div className="stage-edit-form">
                      <div className="inline-stage-field">
                        <label className="inline-stage-label">Start Date <span className="required-asterisk">*</span></label>
                        <input
                          type="date"
                          className="inline-stage-date-input"
                          value={editStartDate}
                          onChange={(e) => setEditStartDate(e.target.value)}
                        />
                      </div>
                      <div className="inline-stage-field">
                        <label className="inline-stage-label">End Date <span className="required-asterisk">*</span></label>
                        <input
                          type="date"
                          className="inline-stage-date-input"
                          value={editEndDate}
                          onChange={(e) => setEditEndDate(e.target.value)}
                        />
                      </div>
                      <div className="inline-stage-field">
                        <label className="inline-stage-label">Description <span className="required-asterisk">*</span></label>
                        <textarea
                          className="new-stage-textarea"
                          value={editDesc}
                          onChange={(e) => setEditDesc(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="new-stage-actions">
                        <button type="button" className="new-stage-btn cancel" onClick={handleCancelEdit}>Cancel</button>
                        <button type="button" className="new-stage-btn save" onClick={() => handleSaveEdit(idx)}>Save</button>
                      </div>
                    </div>
                  ) : (
                    /* Normal Body */
                    <div className="stage-box-body">
                      {deletingIdx === idx ? (
                        <div className="stage-delete-confirm">
                          <p>Delete this situation?</p>
                          <div className="stage-delete-actions">
                            <button type="button" className="new-stage-btn cancel" onClick={() => setDeletingIdx(null)}>Cancel</button>
                            <button type="button" className="new-stage-btn delete-confirm-btn" onClick={() => handleConfirmDelete(idx)}>Delete</button>
                          </div>
                        </div>
                      ) : (
                        stage.description
                      )}
                    </div>
                  )}
                </div>

                {/* Arrow to Next */}
                {idx < stages.length - 1 && (
                  <div className="timeline-arrow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}

            {/* Inline Input Card or Circular Plus Button */}
            {!(item.status === 0 || item.status === '0' || item.is_delete === 1) && (
              isAddingStage ? (
                <React.Fragment>
                  {stages.length > 0 && (
                    <div className="timeline-arrow">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </div>
                  )}

                  <div className="new-stage-input-box">
                    <span className="new-stage-input-title">Stage {(stages.length + 1).toString().padStart(2, '0')}</span>
                    
                    <div className="inline-stage-field">
                      <label className="inline-stage-label">
                        Start Date <span className="required-asterisk">*</span>
                      </label>
                      <input
                        type="date"
                        className="inline-stage-date-input"
                        value={newStageStartDate}
                        onChange={(e) => setNewStageStartDate(e.target.value)}
                      />
                    </div>

                    <div className="inline-stage-field">
                      <label className="inline-stage-label">
                        End Date <span className="required-asterisk">*</span>
                      </label>
                      <input
                        type="date"
                        className="inline-stage-date-input"
                        value={newStageEndDate}
                        onChange={(e) => setNewStageEndDate(e.target.value)}
                      />
                    </div>

                    <div className="inline-stage-field">
                      <label className="inline-stage-label">
                        Description <span className="required-asterisk">*</span>
                      </label>
                      <textarea
                        className="new-stage-textarea"
                        placeholder="Enter stage details..."
                        value={newStageDesc}
                        onChange={(e) => setNewStageDesc(e.target.value)}
                        autoFocus
                      />
                    </div>

                    <div className="new-stage-actions">
                      <button type="button" className="new-stage-btn cancel" onClick={() => setIsAddingStage(false)}>
                        Cancel
                      </button>
                      <button type="button" className="new-stage-btn save" onClick={handleSaveInlineStage}>
                        Save
                      </button>
                    </div>
                  </div>
                </React.Fragment>
              ) : (
                <button 
                  type="button" 
                  className="add-stage-btn" 
                  onClick={handleOpenInlineAdd}
                  aria-label="Add timeline step"
                  title="Add new situation stage"
                >
                  +
                </button>
              )
            )}
          </div>
        </div>
      )}

      {/* Create Situation Popup Modal */}
      {showSituationModal && (
        <div 
          className="modal-backdrop-overlay" 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100vh', 
            backgroundColor: 'rgba(15, 23, 42, 0.55)', 
            backdropFilter: 'blur(8px)', 
            WebkitBackdropFilter: 'blur(8px)', 
            zIndex: 10000, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}
        >
          <div 
            className="modal-card-container" 
            style={{ 
              width: '90%', 
              maxWidth: '500px', 
              backgroundColor: '#ffffff', 
              background: '#ffffff', 
              borderRadius: '16px', 
              padding: '24px', 
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)', 
              border: '1px solid #e2e8f0', 
              position: 'relative', 
              zIndex: 10001, 
              color: '#1e293b', 
              display: 'flex', 
              flexDirection: 'column' 
            }}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#0f172a' }}>
                Create Situation
              </h3>
              <button 
                type="button" 
                className="modal-close-icon-btn" 
                onClick={() => setShowSituationModal(false)}
                aria-label="Close modal"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {sitError && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '10px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{sitError}</span>
              </div>
            )}

            <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                Start Date <span style={{ color: '#ef4444', marginLeft: '4px', fontWeight: '700' }}>*</span>
              </label>
              <input
                type="date"
                style={{ width: '100%', height: '40px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', color: '#1e293b' }}
                value={sitStartDate}
                onChange={(e) => setSitStartDate(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                End Date <span style={{ color: '#ef4444', marginLeft: '4px', fontWeight: '700' }}>*</span>
              </label>
              <input
                type="date"
                style={{ width: '100%', height: '40px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', color: '#1e293b' }}
                value={sitEndDate}
                onChange={(e) => setSitEndDate(e.target.value)}
              />
            </div>

            <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                Description <span style={{ color: '#ef4444', marginLeft: '4px', fontWeight: '700' }}>*</span>
              </label>
              <textarea
                style={{ width: '100%', height: '90px', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', color: '#1e293b' }}
                placeholder="Enter situation details..."
                value={sitDescription}
                onChange={(e) => setSitDescription(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                style={{ padding: '8px 18px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '13px', color: '#475569' }}
                onClick={() => setShowSituationModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ padding: '8px 18px', border: 'none', borderRadius: '8px', background: '#0b66e3', color: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                onClick={handleSaveSituationModal}
              >
                Save Situation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CaseDetails;
